import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import PublicNav from "@/components/PublicNav";
import { CheckCircle2, XCircle, Award, Loader2, ShieldCheck, ArrowLeft } from "lucide-react";

const certBeneficiary = (cert) => cert?.student_name || cert?.beneficiary_name || cert?.association_name || "—";

export default function Verify() {
  const { cert_id } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/verify/${cert_id}`);
        setResult(data);
      } catch (e) { setResult({ valid: false, reason: formatApiError(e) }); }
      finally { setLoading(false); }
    })();
  }, [cert_id]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="verify-page">
      <PublicNav />
      <section className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        {loading ? (
          <div className="grid place-items-center py-24"><Loader2 className="h-10 w-10 animate-spin text-teal-700" /></div>
        ) : result?.valid ? (
          <div className="bg-white rounded-3xl border border-cream-200 card-shadow-lg overflow-hidden">
            <div className="p-5 bg-gradient-to-br from-emerald-600 to-emerald-700 text-white text-center">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur text-xs font-bold uppercase tracking-widest mb-4">
                <ShieldCheck className="h-4 w-4" /> شهادة موثّقة
              </div>
              <CheckCircle2 className="h-16 w-16 mx-auto mb-4" strokeWidth={1.5} />
              <h1 className="text-2xl font-heading font-black">الشهادة صحيحة</h1>
              <p className="mt-2 text-emerald-50">تم التحقق من صحّة هذه الشهادة عبر عماد.</p>
            </div>
            <div className="p-5 space-y-5">
              <Row label="نوع الشهادة" value={result.certificate.kind_label || "شهادة"} />
              <Row label="المُستفيد" value={certBeneficiary(result.certificate)} big />
              <Row label="البرنامج التدريبي" value={result.certificate.program_name} />
              {result.certificate.hours && <Row label="الساعات التدريبية" value={`${result.certificate.hours} ساعة`} />}
              <Row label="تاريخ الإصدار" value={new Date(result.certificate.issued_at).toLocaleDateString("ar-SA")} />
              <Row label="رقم الشهادة" value={result.certificate.cert_id} mono />
            </div>
            <div className="p-4 border-t border-cream-200 bg-cream-50 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Award className="h-4 w-4 text-teal-700" />
                <span>صادرة عن عماد</span>
              </div>
              <Link to="/" className="inline-flex items-center gap-1 text-sm font-semibold text-teal-700 hover:gap-2 transition-all">
                زيارة المنصة <ArrowLeft className="h-4 w-4" />
              </Link>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-red-100 overflow-hidden">
            <div className="p-5 bg-gradient-to-br from-red-600 to-red-700 text-white text-center">
              <XCircle className="h-16 w-16 mx-auto mb-4" strokeWidth={1.5} />
              <h1 className="text-2xl font-heading font-black">الشهادة غير صالحة</h1>
              <p className="mt-2 text-red-50">{result?.reason || "لم يتم العثور على هذه الشهادة."}</p>
            </div>
            <div className="p-4 border-t border-red-100 bg-slate-50">
              <div className="text-sm text-slate-600 mb-4">قد يعود ذلك لأحد الأسباب التالية:</div>
              <ul className="space-y-1.5 text-sm text-slate-600 list-disc me-5">
                <li>رقم الشهادة غير صحيح.</li>
                <li>الشهادة تم إلغاؤها من قِبل الإدارة.</li>
                <li>رابط التحقق منتهي الصلاحية.</li>
              </ul>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function Row({ label, value, big, mono }) {
  return (
    <div className="flex items-start gap-4">
      <div className="w-40 text-sm text-slate-500 shrink-0">{label}</div>
      <div className={`flex-1 font-semibold text-slate-900 ${big ? "text-xl" : ""} ${mono ? "font-mono text-sm" : ""}`} dir={mono ? "ltr" : undefined}>
        {value}
      </div>
    </div>
  );
}
